import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';

import {interpolate} from 'as-ui-kit/utils/strings';
import Popover from '@components/popover';
import Icon from '@components/icon';
import Link from '@components/link';

import { useLocalized } from 'utils/useLocalized';
import FeedbackForm from './FeedbackForm';
import { FeedbackContext } from 'contexts';

import i18nStrings from './strings';
import styles from './styles.modules.scss';

const TopFirmPopover = () => {
    const strings = useLocalized(i18nStrings);
    const { feedbackFormVisible, setFeedbackFormVisible, topFirmsCount } = useContext(FeedbackContext);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [timer, setTimer] = useState(null);
    const feedbackRef = useRef();

    const closePopover = useCallback((e) => {
        if (e) {
            e.stopPropagation();
        }
        setPopoverVisible(false);
    }, []);

    const handleVisibilityChange = useCallback((visible) => {
        if (!visible) {
            feedbackRef.current?.handleClosePopover();
        }
        setPopoverVisible(visible);
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (timer) {
            window.clearTimeout(timer);
        }
        if (!popoverVisible) {
            setPopoverVisible(true);
        }
    }, [timer, popoverVisible]);

    const handleMouseLeave = useCallback(() => {
        if (feedbackFormVisible) return;
        setTimer(window.setTimeout(closePopover, 300));
    }, [feedbackFormVisible, closePopover]);

    const featureDescription = useMemo(() => {
        if (topFirmsCount === 1)
            return strings.description.one;
        return topFirmsCount > 0
            ? interpolate(strings.description.count, { count: topFirmsCount })
            : strings.description.default;
    }, [strings, topFirmsCount]);

    const popoverContent = useMemo(() => (
        <>
            <Popover.Header className={styles['header']}>
                {strings.header}
            </Popover.Header>
            <Popover.Content className={styles['content']}>
                {featureDescription}
            </Popover.Content>
            <Popover.Footer className={styles['footer']}>
                {!feedbackFormVisible ? (
                    <Link
                        as="button"
                        onClick={() => setFeedbackFormVisible(true)}
                    >
                        {strings.ask}
                    </Link>
                ) : (
                    <FeedbackForm
                        ref={feedbackRef}
                        onCancel={() => setFeedbackFormVisible(false)}
                    />
                )}
            </Popover.Footer>
        </>
    ), [feedbackFormVisible, setFeedbackFormVisible, featureDescription, strings]);

    return (
        <span
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseOver={handleMouseEnter}
        >
            <Popover
                isClosedOnOutsideClick
                className={styles['popover']}
                content={popoverContent}
                hideCloseButton={true}
                size="no-size"
                theme="light"
                visible={popoverVisible}
                onCloseClick={closePopover}
                onVisibilityChange={handleVisibilityChange}
            >
                <div>
                    <Icon
	                    color="#64B82A"
                        height={16}
	                    name="ic_pointer-arrow-up"
                        width={12}
                    />
                </div>
            </Popover>
        </span>
    );
};

export default TopFirmPopover;
